import React, { useContext, useState, useEffect } from 'react';
import { Web3Context } from '../../context/Web3Context';
import { PACKAGE_NAMES, PACKAGE_COLORS } from '../../utils/constants';
import './Team.css';

// 2x matrix: max users per level = 2^level
const MATRIX_MAX = [2, 4, 8, 16, 32, 64, 128, 256, 512, 1024];

export default function Team({ notify }) {
  const { userId, readContracts } = useContext(Web3Context);
  const [directCount, setDirectCount] = useState(0);
  const [totalTeam, setTotalTeam] = useState(0);
  const [tab, setTab] = useState('level');
  const [selectedPkg, setSelectedPkg] = useState(0);
  const [levelCounts, setLevelCounts] = useState(Array(30).fill(0));
  const [autoLevelCounts, setAutoLevelCounts] = useState(Array(10).fill(0));
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [levelUsers, setLevelUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId || !readContracts.views) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const [info, sponsorCounts, matrixCounts] = await Promise.all([
          readContracts.views.getUserInfo(userId),
          (readContracts.core.getLevelTeamCounts ? readContracts.core.getLevelTeamCounts(userId).catch(() => Array(30).fill(0)) : Promise.resolve(Array(30).fill(0))),
          (readContracts.pools.getAutoLevelCountAllLevels ? readContracts.pools.getAutoLevelCountAllLevels(userId, selectedPkg).catch(() => Array(10).fill(0)) : Promise.resolve(Array(10).fill(0)))
        ]);
        if (cancelled) return;
        setDirectCount(info.totalDirectRefs.toNumber());
        const lc = Array.isArray(sponsorCounts) ? sponsorCounts.map(c => c.toNumber ? c.toNumber() : Number(c)) : Array(30).fill(0);
        setLevelCounts(lc);
        setTotalTeam(lc.reduce((a, b) => a + b, 0));
        setAutoLevelCounts(Array.isArray(matrixCounts) ? matrixCounts.map(c => c.toNumber ? c.toNumber() : Number(c)) : Array(10).fill(0));
      } catch (err) {
        if (!cancelled) notify.error('Failed to load team');
      }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [userId, selectedPkg, readContracts.views]);

  function toIds(arr) {
    if (!arr || !Array.isArray(arr)) return [];
    const result = [];
    for (let i = 0; i < arr.length; i++) {
      try { const n = arr[i].toNumber ? arr[i].toNumber() : Number(arr[i]); if (n > 0) result.push(n); } catch {}
    }
    return result;
  }

  async function toggleLevel(levelNum) {
    if (expandedLevel === levelNum) { setExpandedLevel(null); setLevelUsers([]); return; }
    setExpandedLevel(levelNum);
    setLoadingUsers(true);
    setLevelUsers([]);
    try {
      let currentIds = toIds(await readContracts.core.getDirectReferralsList(userId));
      if (levelNum === 1) { setLevelUsers(currentIds); setLoadingUsers(false); return; }
      for (let lv = 2; lv <= levelNum; lv++) {
        const nextIds = [];
        for (let b = 0; b < currentIds.length; b += 20) {
          const results = await Promise.all(currentIds.slice(b, b + 20).map(uid => readContracts.core.getDirectReferralsList(uid).catch(() => [])));
          for (const refs of results) for (const id of toIds(refs)) nextIds.push(id);
        }
        if (nextIds.length === 0) { setLevelUsers([]); setLoadingUsers(false); return; }
        currentIds = nextIds;
      }
      setLevelUsers(currentIds);
    } catch { setLevelUsers([]); notify.warning('Could not load users'); }
    setLoadingUsers(false);
  }

  useEffect(() => { setExpandedLevel(null); setLevelUsers([]); }, [tab, selectedPkg]);

  if (loading) return <div className="page-loading"><div className="mini-spinner" /><span>Loading team...</span></div>;

  const pkColor = PACKAGE_COLORS[selectedPkg];
  const matrixTotal = autoLevelCounts.reduce((a, b) => a + b, 0);

  return (
    <div className="team-page">
      <h2 className="page-title">My Team</h2>

      <div className="team-stats-row animate-in">
        <div className="team-stat-card">
          <span className="team-stat-value" style={{ color: '#00e5ff' }}>{directCount}</span>
          <span className="team-stat-label">Direct Referrals</span>
        </div>
        <div className="team-stat-card">
          <span className="team-stat-value" style={{ color: '#a855f7' }}>{totalTeam}</span>
          <span className="team-stat-label">Total Team</span>
        </div>
      </div>

      <div className="team-tabs animate-in">
        <button className={`team-tab ${tab === 'level' ? 'team-tab-active' : ''}`} onClick={() => setTab('level')}>Level (Sponsor)</button>
        <button className={`team-tab ${tab === 'matrix' ? 'team-tab-active' : ''}`} onClick={() => setTab('matrix')}>Matrix Level</button>
      </div>

      <div className="team-pkg-tabs animate-in">
        {PACKAGE_NAMES.map((name, i) => (
          <button key={i} className={`board-tab ${selectedPkg === i ? 'board-tab-active' : ''}`}
            style={selectedPkg === i ? { borderColor: PACKAGE_COLORS[i], color: PACKAGE_COLORS[i] } : {}}
            onClick={() => setSelectedPkg(i)}>{name}</button>
        ))}
      </div>

      {/* ===== MATRIX TAB ===== */}
      {tab === 'matrix' && (
        <div className="matrix-section animate-in">
          <div className="matrix-total-card" style={{ borderColor: pkColor + '25' }}>
            <span className="matrix-total-num" style={{ color: pkColor }}>{matrixTotal}</span>
            <span className="matrix-total-label">Total Matrix Team</span>
          </div>
          <div className="matrix-levels">
            {MATRIX_MAX.map((max, i) => {
              const count = autoLevelCounts[i] || 0;
              const pct = Math.min((count / max) * 100, 100);
              const full = count >= max;
              return (
                <div key={i} className="matrix-level" style={{ animationDelay: `${i * 0.04}s` }}>
                  <div className="matrix-level-head">
                    <span className="matrix-level-num">L{i + 1}</span>
                    <span className="matrix-level-ratio" style={{ color: full ? '#34d399' : 'rgba(255,255,255,0.5)' }}>
                      {count}<span className="matrix-level-max">/{max}</span>
                    </span>
                  </div>
                  <div className="matrix-bar-wrap">
                    <div className="matrix-bar-track">
                      {/* Segment dots showing capacity */}
                      {max <= 16 && Array.from({ length: max }).map((_, si) => (
                        <span key={si} className="matrix-bar-dot" style={{
                          left: `${((si + 0.5) / max) * 100}%`,
                          background: si < count ? pkColor : 'rgba(255,255,255,0.06)'
                        }} />
                      ))}
                      <div className="matrix-bar-fill" style={{
                        width: `${pct}%`,
                        background: full
                          ? 'linear-gradient(90deg, #34d399, #00e5ff)'
                          : `linear-gradient(90deg, ${pkColor}cc, ${pkColor}40)`
                      }} />
                    </div>
                  </div>
                  {full && <span className="matrix-full-tag">FULL</span>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== LEVEL TAB ===== */}
      {tab === 'level' && (
        <div className="team-levels animate-in">
          {Array.from({ length: 30 }).map((_, i) => {
            const lvl = i + 1;
            const count = levelCounts[i] || 0;
            const maxCount = Math.max(...levelCounts, 1);
            const isExpanded = expandedLevel === lvl;
            return (
              <div key={i} className={`team-level-item ${isExpanded ? 'team-level-expanded' : ''}`}>
                <div className="team-level-row" onClick={() => count > 0 && toggleLevel(lvl)}>
                  <span className="team-level-num">Level {lvl}</span>
                  <div className="team-level-bar-bg">
                    <div className="team-level-bar-fill" style={{
                      width: `${Math.min((count / maxCount) * 100, 100)}%`,
                      background: 'linear-gradient(90deg, #00e5ff, #a855f7)'
                    }} />
                  </div>
                  <span className="team-level-count">{count}</span>
                  {count > 0 && <span className={`team-level-arrow ${isExpanded ? 'team-level-arrow-open' : ''}`}>&#9662;</span>}
                </div>
                {isExpanded && (
                  <div className="team-level-users">
                    {loadingUsers ? (
                      <div className="team-level-loading-wrap"><div className="mini-spinner-sm" /><span className="team-level-loading">Loading...</span></div>
                    ) : levelUsers.length === 0 ? (
                      <span className="team-level-loading">No users at this level</span>
                    ) : (
                      <>
                        <span className="team-level-count-badge">{levelUsers.length} users</span>
                        <div className="team-user-grid">
                          {levelUsers.map((uid, j) => (<span key={j} className="team-user-id">ID: {uid}</span>))}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
